import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { General, MediaDevices } from 'icons/untitledui';

import * as Styled from 'shiftNotes/styles/NoteCaptureAttachmentsPreview.style';

export interface NoteCaptureAttachmentsPreviewProps {
  files: File[];
  onRemove: (file: File) => void;
}

interface Preview {
  file: File;
  url: string;
  isVideo: boolean;
}

export default function NoteCaptureAttachmentsPreview({
  files,
  onRemove,
}: NoteCaptureAttachmentsPreviewProps): JSX.Element | null {
  const { t } = useTranslation('pageShiftNotes');
  const [previews, setPreviews] = useState<Preview[]>([]);

  useEffect(() => {
    const next = files.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      isVideo: file.type.startsWith('video/'),
    }));
    setPreviews(next);

    return () => {
      next.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, [files]);

  if (previews.length === 0) return null;

  return (
    <Styled.Container data-testid="note-capture-attachments-preview">
      {previews.map((preview) => (
        <Styled.Item key={preview.url}>
          {preview.isVideo ? (
            <Styled.VideoThumbnail>
              <MediaDevices.VideoRecorder />
              <Styled.VideoLabel>
                {t('noteCaptureAttachments.videoLabel')}
              </Styled.VideoLabel>
            </Styled.VideoThumbnail>
          ) : (
            <Styled.Image
              src={preview.url}
              alt={t('noteCaptureAttachments.photoPreviewAlt')}
            />
          )}
          <Styled.RemoveButton
            type="button"
            onClick={() => onRemove(preview.file)}
            aria-label={t('noteCaptureAttachments.removeAttachment')}
          >
            <General.XClose />
          </Styled.RemoveButton>
        </Styled.Item>
      ))}
    </Styled.Container>
  );
}
